import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as leadsApi from './leads.api';
import { APPROVALS_QK } from './use-lead-approvals';

import type {
  UpdateBacklogDripConfigRequest,
  BulkAssignExitUnassignedRequest,
  BulkReassignLeadsRequest,
  CallLeadTarget,
  CompleteFollowUpRequest,
  CreateLeadRequest,
  CreateStatusChangeRequestRequest,
  LeadDetail,
  LeadListQuery,
  ReviewStatusChangeRequestRequest,
  ScheduleFollowUpRequest,
  TransitionStageRequest,
  UpdateLeadRequest,
} from '@utazzo/shared';

import { extractServerError, focusFormField } from '@/lib/api-error';

/**
 * TanStack Query hooks for the leads admin feature.
 *
 * The verb endpoints (transition / schedule follow-up / complete follow-up /
 * cancel follow-up) all return the full `LeadDetail` after their atomic
 * side-effects so the cache stays in lock-step with the server view - the
 * server is the only place that owns the lead's denormalised state
 * (`current_bucket`, `next_follow_up_at`, etc.).
 */

const QK = {
  list: (query: LeadListQuery) => ['leads', 'list', query] as const,
  detail: (id: string) => ['leads', 'detail', id] as const,
  pipelineStats: () => ['leads', 'pipeline-stats'] as const,
  calls: (id: string) => ['leads', 'calls', id] as const,
  whatsapp: (id: string, target: leadsApi.LeadPhoneTarget = 'PRIMARY') =>
    ['leads', 'whatsapp', id, target] as const,
};

/**
 * 2026-06-25: every lead mutation that can shift a count (stage
 * transition, follow-up complete / schedule / cancel, status-change
 * request approve / reject / create, lead update) routes through
 * this helper so the inbox-aside pill counts, the KPI strip, the
 * Daily Tracker tiles, and the Carry Forward strip all refetch in
 * lockstep. Centralised so adding a new count surface only needs
 * its query key added here.
 */
const invalidateLeadCounts = (qc: ReturnType<typeof useQueryClient>): void => {
  // List queries drive: inbox aside pills, KPI strip, tracker
  // tile per-status counts, carry-forward strip.
  qc.invalidateQueries({ queryKey: ['leads', 'list'] });
  // Pipeline stats drive: Daily Tracker operational tiles
  // (Today's followups, Missed followups, Revert Pending).
  qc.invalidateQueries({ queryKey: ['leads', 'pipeline-stats'] });
  // Lead approval inbox + sidebar pending badge count.
  qc.invalidateQueries({ queryKey: ['leads', 'status-change-requests'] });
};

const writeDetail = (qc: ReturnType<typeof useQueryClient>, lead: LeadDetail): void => {
  qc.setQueryData(QK.detail(lead.id), lead);
  invalidateLeadCounts(qc);
};

export const useLeadsList = (
  query: LeadListQuery,
  opts?: {
    /**
     * 2026-07-17. Keep showing the previous slice while a new query key
     * loads. Opt-in for callers that grow/shift their query in place
     * (e.g. the inbox aside's "Load more" grows pageSize) so the list
     * doesn't flash to a spinner on every step. Off by default - count
     * badges and tiles prefer their explicit loading state over a stale
     * number.
     */
    keepPreviousData?: boolean;
  },
) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => leadsApi.listLeads(query),
    // Lead lists shift constantly (new follow-ups, status changes); short stale.
    staleTime: 15_000,
    placeholderData: opts?.keepPreviousData ? keepPreviousData : undefined,
  });

/**
 * Scope of the exit-unassigned queue. Byte-for-byte the query
 * `LeadsListPage` runs in `?exitUnassigned=true` mode (see its list-query
 * builder) and the one the "Unassigned" KPI tile counts, so the sidebar
 * badge can never disagree with the page it points at. Server-side RBAC
 * narrows the rows per role (ADMIN: org-wide, SALES_LEAD: direct reports).
 */
const EXIT_UNASSIGNED_COUNT_QUERY: LeadListQuery = {
  page: 1,
  pageSize: 1,
  sortDir: 'desc',
  // "Unassigned" = every OPEN lead with no agent (website excluded), not only
  // the departed-agent release queue. `currentBucket: 'OPEN'` scopes the badge
  // to actionable leads: a closed/lost lead keeps `assignedAgentId = null`, so
  // without this it would keep counting toward the badge forever even though it
  // needs no assignment. Unscoped roles (ADMIN/OPS) get the org-wide count;
  // scoped roles stay fenced to their own agents.
  assignedAgentIsNull: true,
  excludeWebsite: true,
  currentBucket: 'OPEN',
};

/**
 * Depth of the exit-unassigned queue, for the sidebar count badge next to
 * "Unassigned" - same treatment as Pending approvals, so an operator can
 * see how many released leads are waiting without opening the page.
 * Caller passes `enabled` from the SAME permission gate the nav item
 * declares, so the request can never fire for someone who can't see it.
 * Lives on the `['leads','list']` key so `invalidateLeadCounts` refreshes
 * it in lockstep with every other count surface after a reassignment.
 */
export const useExitUnassignedCount = (enabled = true) =>
  useQuery({
    queryKey: QK.list(EXIT_UNASSIGNED_COUNT_QUERY),
    queryFn: () => leadsApi.listLeads(EXIT_UNASSIGNED_COUNT_QUERY),
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: false,
    throwOnError: false,
  });

/**
 * Former owners with leads in the exit-unassigned queue - the options for
 * that page's "Former owner" filter. Keyed under `['leads','list']` on
 * purpose: a bulk reassign empties an owner's rows, and `invalidateLeadCounts`
 * then drops them from the dropdown in the same pass as the grid.
 */
export const useExitUnassignedFormerOwners = (enabled = true) =>
  useQuery({
    queryKey: ['leads', 'list', 'exit-unassigned-former-owners'] as const,
    queryFn: () => leadsApi.getExitUnassignedFormerOwners(),
    enabled,
    staleTime: 15_000,
    retry: false,
    throwOnError: false,
  });

const BACKLOG_DRIP_QK = ['leads', 'list', 'backlog-drip'] as const;

/**
 * Backlog drip progress for the Unassigned page strip. Under `['leads','list']`
 * so a bulk reassign (which shrinks the queue) refreshes it with the grid.
 */
export const useBacklogDrip = (enabled = true) =>
  useQuery({
    queryKey: BACKLOG_DRIP_QK,
    queryFn: () => leadsApi.getBacklogDrip(),
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: false,
    throwOnError: false,
  });

export const useUpdateBacklogDrip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateBacklogDripConfigRequest) => leadsApi.updateBacklogDrip(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BACKLOG_DRIP_QK });
      toast.success('Backlog drip settings saved');
    },
    onError: (err) =>
      toast.error(extractServerError(err).message ?? 'Could not save backlog drip settings'),
  });
};

export const useRunBacklogDrip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => leadsApi.runBacklogDrip(),
    onSuccess: (res) => {
      invalidateLeadCounts(qc);
      void qc.invalidateQueries({ queryKey: BACKLOG_DRIP_QK });
      if (res.assigned > 0) {
        toast.success(
          `${res.assigned} lead${res.assigned === 1 ? '' : 's'} dealt out${res.note ? ` - ${res.note}` : ''}`,
        );
      } else {
        toast.warning(res.note ?? 'Nothing was dealt out');
      }
    },
    onError: (err) => toast.error(extractServerError(err).message ?? 'Backlog drip run failed'),
  });
};

/**
 * Single-fetch pipeline tile counters. Backs every tile in
 * `LeadsBucketKpiStrip`, `LeadsTrackerStrip`, and `LeadsBusinessStrip`
 * via the keys defined in
 * `docs/architecture/leads-pipeline-optimization-plan.md` Appendix B.
 *
 * The server side caches this for 5s in Redis (with auto-fallback to
 * a direct DB read when Redis is offline or the kill switch is
 * thrown), so we use a matching client `staleTime` to avoid
 * unnecessary refetches while keeping the strip lively after the
 * operator's own actions.
 */
export const useLeadsPipelineStats = () =>
  useQuery({
    queryKey: QK.pipelineStats(),
    queryFn: () => leadsApi.getPipelineStats(),
    staleTime: 5_000,
    refetchOnWindowFocus: false,
  });

export const useLead = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => leadsApi.getLead(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

export const useCreateLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLeadRequest) => leadsApi.createLead(payload),
    onSuccess: (lead) => {
      writeDetail(qc, lead);
      toast.success(`Lead ${lead.reference} created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create lead'),
  });
};

/**
 * `suppressSuccessToast` matches the pattern already on
 * `useTransitionLeadStage`: callers chaining this mutation with a follow-up
 * step (a stage transition, an approval request, etc.) can silence the
 * standalone "Lead updated" toast so the operator sees ONE final outcome
 * message instead of two overlapping toasts. See the "Still missing" +
 * "Lead updated" collision the not-ready compound save produced before.
 */
interface UpdateLeadVariables {
  id: string;
  payload: UpdateLeadRequest;
  suppressSuccessToast?: boolean;
}

export const useUpdateLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: UpdateLeadVariables) => leadsApi.updateLead(id, payload),
    onSuccess: (lead, variables) => {
      writeDetail(qc, lead);
      if (!variables.suppressSuccessToast) {
        toast.success('Lead updated');
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update lead'),
  });
};

export const useDeleteLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => leadsApi.deleteLead(id),
    onSuccess: () => {
      invalidateLeadCounts(qc);
      toast.success('Lead deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete lead'),
  });
};

export const useBulkAssignExitUnassigned = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkAssignExitUnassignedRequest) =>
      leadsApi.bulkAssignExitUnassigned(payload),
    onSuccess: (res) => {
      invalidateLeadCounts(qc);
      if (res.failed.length === 0) {
        toast.success(`${res.updated} lead${res.updated === 1 ? '' : 's'} assigned successfully`);
      } else if (res.updated > 0) {
        toast.warning(`${res.updated} assigned, ${res.failed.length} could not be assigned`);
      } else {
        toast.error('No leads could be assigned');
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to assign leads'),
  });
};

/** Inbox aside "Select -> Assign": one owner change per lead, server-side. */
export const useBulkReassignLeads = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      onProgress,
      ...payload
    }: BulkReassignLeadsRequest & { onProgress?: (done: number, total: number) => void }) =>
      leadsApi.bulkReassignLeads(payload, onProgress),
    onSuccess: (res) => {
      invalidateLeadCounts(qc);
      // The open lead's header shows its owner; it may be in the selection.
      qc.invalidateQueries({ queryKey: ['leads', 'detail'] });
      const moved = `${res.updated} lead${res.updated === 1 ? '' : 's'} reassigned`;
      if (res.failed.length === 0) {
        toast.success(res.updated > 0 ? moved : 'Already with this agent - nothing to change');
      } else if (res.updated > 0) {
        toast.warning(`${moved}, ${res.failed.length} could not be reassigned`);
      } else {
        toast.error('No leads could be reassigned');
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to reassign leads'),
  });
};

// ----- Verb endpoints - atomic on the server -----

export type TransitionLeadStageVariables = {
  id: string;
  payload: TransitionStageRequest;
  /** When true, caller owns success UX (e.g. LeadStatusChanger undo toast). */
  suppressSuccessToast?: boolean;
};

const formatLeadStageLabel = (lead: {
  currentStatus: { name: string };
  currentSubStatus?: { name: string } | null;
}): string =>
  lead.currentSubStatus
    ? `${lead.currentStatus.name} / ${lead.currentSubStatus.name}`
    : lead.currentStatus.name;

export const useTransitionLeadStage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: TransitionLeadStageVariables) =>
      leadsApi.transitionLeadStage(id, payload),
    onSuccess: (lead, variables) => {
      writeDetail(qc, lead);
      // 2026-06-25: invalidate the leads-list queries so the inbox
      // aside's per-pill counts (and the dashboard tracker tiles)
      // refetch with the new stage distribution. Without this the
      // counts stayed stale until the next URL change or refresh.
      invalidateLeadCounts(qc);
      // 2026-07-10. Also invalidate the detail query itself. The
      // mutation payload is fresh, but this second invalidation
      // triggers an out-of-band refetch so any side-effect writes on
      // the server (e.g. `leadStatusChangeRequest` supersession
      // superseded rows dropping off) are guaranteed to be reflected
      // in the cache. Belt + braces against the "stale pending
      // banner" class of bugs.
      qc.invalidateQueries({ queryKey: QK.detail(lead.id) });
      if (!variables.suppressSuccessToast) {
        toast.success(`Moved to ${formatLeadStageLabel(lead)}`);
      }
    },
    onError: (err: unknown) => {
      if (isOptionalEnquiryOnlyActiveReadinessError(err)) return;
      toast.error(extractMessage(err) ?? 'Failed to move stage');
    },
  });
};

/**
 * 2026-07-09. `suppressSuccessToast` matches the pattern already on
 * `useUpdateLead` / `useTransitionLeadStage`: callers chaining these
 * mutations with a follow-on action (log follow-up + fire transition)
 * silence the individual "Follow-up scheduled" / "Follow-up logged"
 * toasts so the operator sees ONE final outcome message instead of
 * three overlapping toasts.
 */
interface ScheduleFollowUpVariables {
  id: string;
  payload: ScheduleFollowUpRequest;
  suppressSuccessToast?: boolean;
}
interface CompleteFollowUpVariables {
  id: string;
  followUpId: string;
  payload: CompleteFollowUpRequest;
  suppressSuccessToast?: boolean;
}

export const useScheduleFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: ScheduleFollowUpVariables) =>
      leadsApi.scheduleFollowUp(id, payload),
    onSuccess: (lead, variables) => {
      writeDetail(qc, lead);
      // Followup state denorm refresh affects the
      // followup-related counts (Today's, Missed, etc.) on the
      // tracker tiles + inbox aside Tile dropdown.
      invalidateLeadCounts(qc);
      if (!variables.suppressSuccessToast) {
        toast.success('Follow-up scheduled');
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to schedule follow-up'),
  });
};

export const useCompleteFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, followUpId, payload }: CompleteFollowUpVariables) =>
      leadsApi.completeFollowUp(id, followUpId, payload),
    onSuccess: (lead, variables) => {
      writeDetail(qc, lead);
      // Completing a followup can flip:
      //   - nextFollowUpAt + followup_state denorm (affects
      //     Today's/Missed/Total tiles)
      //   - currentStatus/Bucket when isLostReason fires (affects
      //     per-status pill counts)
      invalidateLeadCounts(qc);
      if (!variables.suppressSuccessToast) {
        // ADR-0069. A lost-reason outcome from an operator who can't close
        // the lead directly comes back OPEN with a fresh PENDING LOST
        // request - say so instead of a silent "logged".
        const raisedLostApproval =
          Boolean(variables.payload.lostReasonId) &&
          lead.currentBucket === 'OPEN' &&
          lead.pendingStatusChangeRequest?.kind === 'LOST_TRANSITION';
        toast.success(
          raisedLostApproval ? 'Follow-up logged - LOST approval request sent' : 'Follow-up logged',
        );
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to log follow-up'),
  });
};

export const useCancelFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, followUpId }: { id: string; followUpId: string }) =>
      leadsApi.cancelFollowUp(id, followUpId),
    onSuccess: (lead) => {
      writeDetail(qc, lead);
      invalidateLeadCounts(qc);
      toast.success('Follow-up cancelled');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to cancel follow-up'),
  });
};

// ADR-0054: out-of-band status-change approval workflow.

export const useCreateStatusChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateStatusChangeRequestRequest }) =>
      leadsApi.createStatusChangeRequest(id, payload),
    onSuccess: (lead) => {
      writeDetail(qc, lead);
      invalidateLeadCounts(qc);
      void qc.invalidateQueries({ queryKey: APPROVALS_QK.all() });
      toast.success('Approval request submitted for review');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to submit approval request'),
  });
};

/**
 * Assignee options for approving ONE pending destination-change request.
 * `null` ids keep it idle, so the dialog can call it unconditionally and only
 * fetch on the approve step of a destination change.
 */
export const useDestinationChangeAssigneeOptions = (
  leadId: string | null,
  requestId: string | null,
) =>
  useQuery({
    queryKey: ['leads', 'destination-change-assignees', leadId, requestId] as const,
    queryFn: () =>
      leadsApi.getDestinationChangeAssigneeOptions(leadId as string, requestId as string),
    enabled: Boolean(leadId && requestId),
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });

export const useApproveStatusChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      requestId,
      payload,
    }: {
      id: string;
      requestId: string;
      payload: ReviewStatusChangeRequestRequest;
    }) => leadsApi.approveStatusChangeRequest(id, requestId, payload),
    onSuccess: (lead) => {
      writeDetail(qc, lead);
      invalidateLeadCounts(qc);
      void qc.invalidateQueries({ queryKey: APPROVALS_QK.all() });
      toast.success('Approval request approved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to approve request'),
  });
};

export const useRejectStatusChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      requestId,
      payload,
    }: {
      id: string;
      requestId: string;
      payload: ReviewStatusChangeRequestRequest;
    }) => leadsApi.rejectStatusChangeRequest(id, requestId, payload),
    onSuccess: (lead) => {
      writeDetail(qc, lead);
      invalidateLeadCounts(qc);
      void qc.invalidateQueries({ queryKey: APPROVALS_QK.all() });
      toast.success('Approval request rejected');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to reject request'),
  });
};

/**
 * 2026-07-10. Requester-side cancel. Used by the "Cancel my request"
 * button on the lead detail's pending banner - lets the original
 * requester withdraw a request they submitted without needing the
 * reviewer to act. The server enforces the ownership check.
 */
export const useCancelStatusChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, requestId }: { id: string; requestId: string }) =>
      leadsApi.cancelStatusChangeRequest(id, requestId),
    onSuccess: (lead) => {
      writeDetail(qc, lead);
      invalidateLeadCounts(qc);
      void qc.invalidateQueries({ queryKey: APPROVALS_QK.all() });
      toast.success('Approval request cancelled');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to cancel request'),
  });
};

/** Sales-agent only - re-notifies the assigned reviewer of a PENDING request. */
export const useNudgeStatusChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, requestId }: { id: string; requestId: string }) =>
      leadsApi.nudgeStatusChangeRequest(id, requestId),
    onSuccess: () => {
      invalidateLeadCounts(qc);
      toast.success('Reminder sent to your approver');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to send reminder'),
  });
};

/**
 * CallerDesk click-to-call. No cache writes - the call itself changes nothing
 * in our DB (yet); the outcome is logged by the agent as a follow-up.
 */
export const useCallLead = () => {
  return useMutation({
    mutationFn: ({ id, target }: { id: string; target?: CallLeadTarget; leadName?: string }) =>
      leadsApi.callLead(id, target),
    onSuccess: (result, variables) => {
      // Show WHO is being called so the agent knows which lead when their
      // softphone rings - MicroSIP itself only shows the DID, not the lead.
      const who = variables.leadName ? `${variables.leadName} · ` : '';
      const numLabel = `${result.dialedNumber}${result.target === 'ALTERNATE' ? ' (alternate)' : ''}`;
      toast.success(`📞 Calling ${who}${numLabel}`, {
        duration: 15000,
        description: `Your softphone will ring first (${result.memberName}) - answer it, then the lead is connected. No ring in a few seconds? Check VPN + MicroSIP is Online.`,
      });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not start the call'),
  });
};

/**
 * Live CallerDesk call history for the lead-detail "Calls" tab. Each request
 * hits CallerDesk upstream (nothing is stored locally), so the tab mounts
 * this lazily and we keep a short staleTime instead of refetching on every
 * focus.
 */
export const useLeadCalls = (id: string, enabled = true) => {
  return useQuery({
    queryKey: QK.calls(id),
    queryFn: () => leadsApi.listLeadCalls(id),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
};

/**
 * WhatsApp thread for the lead chat tab. Polls while the tab is mounted so
 * incoming replies appear without a manual refresh - the tab content only
 * mounts when selected (Radix), so the poll never runs in the background.
 */
export const useLeadWhatsApp = (
  id: string,
  enabled = true,
  target: leadsApi.LeadPhoneTarget = 'PRIMARY',
) => {
  return useQuery({
    queryKey: QK.whatsapp(id, target),
    queryFn: () => leadsApi.getLeadWhatsAppThread(id, target),
    enabled,
    refetchInterval: 8_000,
    retry: 1,
  });
};

export const useSendLeadWhatsApp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
      target,
    }: {
      id: string;
      payload: leadsApi.SendLeadWhatsAppMessagePayload;
      target?: leadsApi.LeadPhoneTarget;
    }) => leadsApi.sendLeadWhatsAppMessage(id, payload, target),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: QK.whatsapp(variables.id, variables.target ?? 'PRIMARY'),
      });
    },
    // Silent by design: WhatsApp delivery failures belong to the admin
    // WhatsApp log, not to the agent mid-conversation.
    onError: (err: unknown) => console.warn('WhatsApp send failed', err),
  });
};

/**
 * Thin adapter over the shared `extractServerError` for the leads mutations.
 * Alongside picking the most-specific server message it also focuses the
 * offending form control when the server pinpoints one - so validation
 * failures surface at the exact input the operator missed.
 */
const extractMessage = (err: unknown): string | null => {
  const { message, field } = extractServerError(err);
  if (field) focusFormField(field);
  return message;
};

const OPTIONAL_ENQUIRY_ACTIVE_FIELDS = new Set([
  'queryTypeId',
  'querySourceId',
  'querySubSourceId',
  'queryMediumId',
  'querySourceUrl',
]);

/**
 * Stale API responses may still list optional enquiry fields - don't toast.
 *
 * 2026-07-09. The check now reads `missing[]` out of `details` (the ApiError
 * envelope's nested payload). The previous version read it off the top level
 * (`e.missing`), which is always undefined - the guard therefore returned
 * `true` for EVERY `lead_not_active_ready` error and silently suppressed the
 * toast for real gaps like "Budget missing" too. The fallback to top-level
 * `missing` stays for defence-in-depth against any older API shape.
 */
const isOptionalEnquiryOnlyActiveReadinessError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as {
    code?: unknown;
    missing?: ReadonlyArray<{ field?: string }>;
    details?: { missing?: ReadonlyArray<{ field?: string }> };
  };
  if (e.code !== 'lead_not_active_ready') return false;
  const missing = e.details?.missing ?? e.missing ?? [];
  // Empty missing list on the wire = we can't infer anything useful, so
  // fall through to the generic toast rather than silently swallowing.
  if (missing.length === 0) return false;
  return missing.every((m) => OPTIONAL_ENQUIRY_ACTIVE_FIELDS.has(m.field ?? ''));
};
